import {
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetReviewQueue } from "@/accounts-payable/query";
import type { ApReviewQueueFilters } from "@/accounts-payable/types";
import { flattenPages } from "@/accounts-payable/utils";
import {
  firstCardShowing,
  getQueueReturnIndex,
  needsMoreQueuePages,
  type QueuePosition,
} from "./utils";

const CARD = "[data-review-invoice-id]";

/**
 * Per business, as the invoice list keeps its own. The list unmounts while an
 * invoice is open, and every way back — the sidebar, the breadcrumb, the
 * browser's back, after any number of steps through the carousel — mounts it
 * afresh, so the position can't live in one history entry's state.
 */
const positions = new Map<string, QueuePosition>();

const filterKeyOf = (filters?: ApReviewQueueFilters) =>
  JSON.stringify([
    filters?.actionTypes ?? [],
    filters?.assigneeIds ?? [],
    filters?.vendorIds ?? [],
    filters?.search ?? "",
  ]);

/** Callers key the list by business, so the saved position is read once. */
export const useReviewQueueNavigation = (
  businessId: string | null,
  filters?: ApReviewQueueFilters,
) => {
  const filterKey = filterKeyOf(filters);
  const [savedPosition] = useState(() =>
    businessId ? (positions.get(businessId) ?? null) : null,
  );
  const position =
    savedPosition?.filterKey === filterKey ? savedPosition : null;
  const [restored, setRestored] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const queue = useGetReviewQueue(businessId, !!position, filters);
  const items = useMemo(() => flattenPages(queue.data?.pages), [queue.data]);
  // Built once per page load, not on every scroll event.
  const invoiceIds = useMemo(() => items.map((item) => item.id), [items]);
  const indexById = useMemo(
    () => new Map(invoiceIds.map((id, index) => [id, index])),
    [invoiceIds],
  );
  const loadedPageCount = queue.data?.pages.length ?? 0;
  const needsMorePages = position
    ? needsMoreQueuePages({
        position,
        items,
        loadedPageCount,
        hasNextPage: queue.hasNextPage,
      })
    : false;
  const restoring = !!position && !restored;
  const awaitingPosition =
    restoring &&
    !queue.isError &&
    (queue.isPending || queue.isFetching || needsMorePages);
  const { fetchNextPage } = queue;

  // A reload or an expired cache may have kept only the first page. Fetch the
  // saved depth before attempting to scroll; otherwise the browser clamps it.
  useEffect(() => {
    if (restoring && needsMorePages && !queue.isFetching && !queue.isError) {
      void fetchNextPage();
    }
  }, [
    restoring,
    needsMorePages,
    loadedPageCount,
    queue.isFetching,
    queue.isError,
    fetchNextPage,
  ]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (
      !position ||
      !restoring ||
      awaitingPosition ||
      queue.isError ||
      !viewport
    )
      return;
    const index = getQueueReturnIndex(position, invoiceIds);
    const card = viewport.querySelectorAll<HTMLElement>(CARD)[index];
    const scrollTop = card
      ? viewport.scrollTop +
        card.getBoundingClientRect().top -
        viewport.getBoundingClientRect().top -
        position.offset
      : position.scrollTop;
    // A page can contain the anchor without enough content below it to reach
    // the saved offset. Load that content before the browser clamps scrolling.
    if (
      scrollTop > viewport.scrollHeight - viewport.clientHeight + 1 &&
      queue.hasNextPage
    ) {
      void fetchNextPage();
      return;
    }
    viewport.scrollTop = scrollTop;
    setRestored(true);
  }, [
    position,
    restoring,
    awaitingPosition,
    queue.isError,
    queue.hasNextPage,
    fetchNextPage,
    invoiceIds,
  ]);

  // "Load more" changes the depth to restore without scrolling.
  useEffect(() => {
    const saved = businessId ? positions.get(businessId) : undefined;
    if (
      !businessId ||
      restoring ||
      queue.isPlaceholderData ||
      saved?.filterKey !== filterKey
    )
      return;
    positions.set(businessId, { ...saved, pageCount: loadedPageCount });
  }, [
    businessId,
    restoring,
    queue.isPlaceholderData,
    filterKey,
    loadedPageCount,
  ]);

  const remember = (card: HTMLElement | null | undefined) => {
    const viewport = viewportRef.current;
    // Cards kept up while a new search or filter loads (placeholder data)
    // belong to the previous list; a position saved from them would pair
    // this list's filters with that list's invoices.
    if (!businessId || !viewport || restoring || queue.isPlaceholderData)
      return;
    const invoiceIndex = indexById.get(card?.dataset.reviewInvoiceId ?? "");
    if (!card || invoiceIndex === undefined) {
      positions.delete(businessId);
      return;
    }
    positions.set(businessId, {
      filterKey,
      invoiceIds,
      invoiceIndex,
      invoiceCreatedAt: items[invoiceIndex].created_at,
      offset:
        card.getBoundingClientRect().top - viewport.getBoundingClientRect().top,
      scrollTop: viewport.scrollTop,
      pageCount: loadedPageCount,
    });
  };

  /** Anchor on the first card still showing; at the top, there's nothing to keep. */
  const onViewportScroll = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    remember(
      viewport.scrollTop > 0
        ? firstCardShowing(
            viewport.querySelectorAll<HTMLElement>(CARD),
            viewport.getBoundingClientRect().top,
          )
        : null,
    );
  };

  /** The opened card is the one to come back to, wherever it sits. */
  const openInvoice = (event: MouseEvent<HTMLAnchorElement>) =>
    remember(event.currentTarget.closest<HTMLElement>(CARD));

  return {
    queue,
    items,
    viewportRef,
    onViewportScroll,
    awaitingPosition,
    openInvoice,
    restoring,
  };
};
